import React from 'react';
import { Home, Hammer, Building2, Palmtree, Sparkles, ArrowRight } from 'lucide-react';
export type InvestorType = 'wholesaler' | 'fix-flip' | 'buy-hold' | 'str' | 'new';
export type DealVolume = '0-2' | '3-8' | '9-20' | '21+';
export type TeamSize = '1' | '2-5' | '6-15' | '16+';
interface QuestionnaireAnswers {
  dealVolume: DealVolume | '';
  investorType: InvestorType | '';
  teamSize: TeamSize | '';
}
interface PlanQuestionnaireProps {
  answers: QuestionnaireAnswers;
  currentQuestion: number;
  onAnswerChange: (field: keyof QuestionnaireAnswers, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}
export function PlanQuestionnaire({
  answers,
  currentQuestion,
  onAnswerChange,
  onNext,
  onBack
}: PlanQuestionnaireProps) {
  const investorTypes = [{
    id: 'wholesaler',
    icon: Home,
    label: 'Wholesaler',
    description: 'High volume, quick turnaround'
  }, {
    id: 'fix-flip',
    icon: Hammer,
    label: 'Fix & Flip',
    description: 'Renovation & resale focus'
  }, {
    id: 'buy-hold',
    icon: Building2,
    label: 'Buy & Hold',
    description: 'Long-term rental income'
  }, {
    id: 'str',
    icon: Palmtree,
    label: 'STR',
    description: 'Short-term rentals (Airbnb)'
  }, {
    id: 'new',
    icon: Sparkles,
    label: 'New Investor',
    description: 'Just getting started'
  }];
  const dealVolumes = [{
    id: '0-2',
    label: '0-2',
    description: 'deals/month'
  }, {
    id: '3-8',
    label: '3-8',
    description: 'deals/month'
  }, {
    id: '9-20',
    label: '9-20',
    description: 'deals/month'
  }, {
    id: '21+',
    label: '21+',
    description: 'deals/month'
  }];
  const teamSizes = [{
    id: '1',
    label: 'Just me',
    description: 'Solo investor'
  }, {
    id: '2-5',
    label: '2-5',
    description: 'Small team'
  }, {
    id: '6-15',
    label: '6-15',
    description: 'Growing team'
  }, {
    id: '16+',
    label: '16+',
    description: 'Large organization'
  }];
  const isAnswered = (question: number) => {
    if (question === 1) return !!answers.dealVolume;
    if (question === 2) return !!answers.investorType;
    if (question === 3) return !!answers.teamSize;
    return false;
  };
  return <div className="space-y-6 animate-fade-in" data-id="element-2354">
      {/* Question 1: Deal Volume */}
      {currentQuestion === 1 && <>
          <div className="text-center mb-8" data-id="element-2355">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4" data-id="element-2356">
              <span className="text-xs font-medium text-[#3ab795]" data-id="element-2357">
                Question 1 of 3
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2358">
              How many deals do you close per month?
            </h3>
            <p className="text-gray-400" data-id="element-2359">
              This helps us recommend the right plan for your volume
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2360">
            {dealVolumes.map(volume => <button key={volume.id} onClick={() => onAnswerChange('dealVolume', volume.id)} className={`p-6 rounded-xl border-2 transition-all duration-300 text-center group ${answers.dealVolume === volume.id ? 'border-[#3ab795] bg-[#3ab795]/10' : 'border-gray-700 bg-[#1a1a1a] hover:border-gray-600'}`} data-id="element-2361">
                <div className="text-3xl font-bold text-white mb-1" data-id="element-2362">
                  {volume.label}
                </div>
                <div className="text-sm text-gray-400" data-id="element-2363">
                  {volume.description}
                </div>
              </button>)}
          </div>

          <button onClick={onNext} disabled={!isAnswered(1)} className="w-full px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2364">
            <span data-id="element-2365">Continue</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2366" />
          </button>
        </>}

      {/* Question 2: Investor Type */}
      {currentQuestion === 2 && <>
          <div className="text-center mb-8" data-id="element-2367">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4" data-id="element-2368">
              <span className="text-xs font-medium text-[#3ab795]" data-id="element-2369">
                Question 2 of 3
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2370">
              What type of investor are you?
            </h3>
            <p className="text-gray-400" data-id="element-2371">
              This helps us highlight features that matter most to you
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2372">
            {investorTypes.map(type => {
          const Icon = type.icon;
          return <button key={type.id} onClick={() => onAnswerChange('investorType', type.id)} className={`p-5 rounded-xl border-2 transition-all duration-300 text-left group ${answers.investorType === type.id ? 'border-[#3ab795] bg-[#3ab795]/10' : 'border-gray-700 bg-[#1a1a1a] hover:border-gray-600'}`} data-id="element-2373">
                  <div className="flex items-start gap-3" data-id="element-2374">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${answers.investorType === type.id ? 'bg-[#3ab795] text-white' : 'bg-gray-800 text-gray-400 group-hover:bg-gray-700'}`} data-id="element-2375">
                      <Icon className="w-5 h-5" data-id="element-2376" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2377">
                      <div className="font-semibold text-white mb-1" data-id="element-2378">
                        {type.label}
                      </div>
                      <div className="text-sm text-gray-400" data-id="element-2379">
                        {type.description}
                      </div>
                    </div>
                  </div>
                </button>;
        })}
          </div>

          <div className="flex gap-3" data-id="element-2380">
            <button onClick={onBack} className="flex-1 px-6 py-4 bg-[#1a1a1a] border border-gray-700 text-white font-semibold rounded-lg hover:border-[#3ab795] transition-all duration-300" data-id="element-2381">
              Back
            </button>
            <button onClick={onNext} disabled={!isAnswered(2)} className="flex-1 px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2382">
              <span data-id="element-2383">Continue</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2384" />
            </button>
          </div>
        </>}

      {/* Question 3: Team Size */}
      {currentQuestion === 3 && <>
          <div className="text-center mb-8" data-id="element-2385">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4" data-id="element-2386">
              <span className="text-xs font-medium text-[#3ab795]" data-id="element-2387">
                Question 3 of 3
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2388">
              How many people will use AcquireFlow?
            </h3>
            <p className="text-gray-400" data-id="element-2389">
              This helps us ensure you have the right capacity
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2390">
            {teamSizes.map(size => <button key={size.id} onClick={() => onAnswerChange('teamSize', size.id)} className={`p-6 rounded-xl border-2 transition-all duration-300 text-center group ${answers.teamSize === size.id ? 'border-[#3ab795] bg-[#3ab795]/10' : 'border-gray-700 bg-[#1a1a1a] hover:border-gray-600'}`} data-id="element-2391">
                <div className="text-xl font-bold text-white mb-1" data-id="element-2392">
                  {size.label}
                </div>
                <div className="text-sm text-gray-400" data-id="element-2393">{size.description}</div>
              </button>)}
          </div>

          <div className="flex gap-3" data-id="element-2394">
            <button onClick={onBack} className="flex-1 px-6 py-4 bg-[#1a1a1a] border border-gray-700 text-white font-semibold rounded-lg hover:border-[#3ab795] transition-all duration-300" data-id="element-2395">
              Back
            </button>
            <button onClick={onNext} disabled={!isAnswered(3)} className="flex-1 px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2396">
              <span data-id="element-2397">See Recommendation</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2398" />
            </button>
          </div>
        </>}
    </div>;
}